import React from 'react';
import { Typography, Card, CardContent, CircularProgress, Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircle as CheckIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { FinancialSummaryCard } from '../step3/FinancialSummaryCard';
import { OrderInformationCard } from '../step3/OrderInformationCard';
import { PaymentInformationCard } from '../step3/PaymentInformationCard';
import { ProductsTableCard } from '../step3/ProductsTableCard';
import { PrintReceiptModal } from '../step3/PrintReceiptModal';
import { CommercialDateModeSection } from '../shared/CommercialDateTimePicker';
import { useSalesModal } from '../../../hooks/useSalesModal';
import { SALES_CUSTOMER_STEP_ENABLED } from '../../../config/feature-flags';
import SaleInvoiceToggleSection from '../SaleInvoiceToggleSection';
import SaleSellerSelect from '../shared/SaleSellerSelect/SaleSellerSelect';
import MissingRequirementsHint from '../shared/MissingRequirementsHint';
import { useOrdersPermissions } from '../../../hooks/useOrdersPermissions';
import { useDataVisibilityPermissions } from '../../../hooks/useDataVisibilityPermissions';
import { ConfirmSaleConfirmationModal } from '../../modals/confirmation';
import { useSalesStep3, SalesOrderCompletedPayload } from './SalesStep3.hooks';
import type { CommercialDateMode } from '../../../utils/sales/commercial-sale-date';
import type { SelectedSeller } from '../../../types/sales.types';
import { formatCurrency } from '../../../utils/formatters/money';
import {
  MainContainer,
  LeftColumn,
  RightColumn,
  ActionsBox,
  BackButton,
  ConfirmButton,
  LossWarningList,
  InfoList,
} from './SalesStep3.styles';

export interface SalesStep3Props {
  onOrderCompleted?: (payload: SalesOrderCompletedPayload) => void;
}

const SalesStep3: React.FC<SalesStep3Props> = ({ onOrderCompleted }) => {
  const {
    setSaleDate,
    setSaleDateMode,
    setSelectedSeller,
    state: modalState,
    setHasInvoice,
  } = useSalesModal();
  const { canModifySaleDate, canEditSaleDate } = useOrdersPermissions();
  // Loss amount = cost - final price, so it reveals the cost: amount only with cost visibility.
  const { canShowCostUnitPrice } = useDataVisibilityPermissions();
  const {
    // State
    state,
    orderSummary,
    isProcessing,
    orderNumber,
    branchReceiptNumber,
    orderCompletedAt,
    showPrintModal,
    showConfirmModal,
    requiresZeroSaleAck,
    zeroSaleAckKind,
    isFullZeroSale,
    lossItems,
    paymentMethods,
    chargeCategories,
    isMixedSale,
    remoteGroupCount,
    canConfirmMixedSale,
    confirmError,
    saleDateModeError,
    sellingLocationId,
    isEditMode,
    sellerRequired,
    missingSeller,
    missingSaleDate,
    // Handlers
    openConfirmModal,
    closeConfirmModal,
    clearSaleDateModeError,
    clearConfirmError,
    handleConfirmSale,
    handleClosePrintModal,
    previousStep,
    setProductDelivered,
  } = useSalesStep3({ onOrderCompleted });

  const handleSaleDateModeChange = (mode: CommercialDateMode) => {
    clearSaleDateModeError();
    setSaleDateMode(mode);
  };

  const handleSellerChange = (seller: SelectedSeller | null) => {
    clearConfirmError();
    setSelectedSeller(seller);
  };

  return (
    <MainContainer>
      {/* Left Column - Pieces first (with delivery), then sale + payment info */}
      <LeftColumn>
        <ProductsTableCard
          selectedProducts={state.selectedProducts}
          sellingLocationId={sellingLocationId}
          onToggleDelivered={isEditMode ? undefined : setProductDelivered}
        />

        {lossItems.length > 0 && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              backgroundColor: alpha(
                theme.palette.warning.main,
                theme.palette.mode === 'dark' ? 0.18 : 0.08,
              ),
              border: `1px solid ${alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
              borderRadius: '8px',
              padding: '10px 14px',
              mt: 2,
            })}
          >
            <InfoIcon sx={{ fontSize: 18, color: 'warning.light', mt: '2px', flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" sx={{ color: 'warning.light', mb: 1, fontWeight: 600 }}>
                Por debajo del costo
              </Typography>
              <LossWarningList>
                {lossItems.map((item) => (
                  <Typography
                    key={item.code}
                    component="li"
                    variant="body2"
                    sx={{ color: 'warning.light', lineHeight: 1.5 }}
                  >
                    {item.name}
                    {canShowCostUnitPrice && ` — ${formatCurrency(item.loss)}`}
                  </Typography>
                ))}
              </LossWarningList>
            </Box>
          </Box>
        )}

        <OrderInformationCard
          orderNumber={orderNumber}
          selectedLocation={state.selectedLocation}
          orderSummary={orderSummary}
          selectedCustomer={state.selectedCustomer}
          hasInvoice={state.hasInvoice}
          orderCharges={state.orderCharges}
          chargeCategories={chargeCategories}
        />

        {!SALES_CUSTOMER_STEP_ENABLED && (
          <SaleInvoiceToggleSection hasInvoice={state.hasInvoice} onChange={setHasInvoice} />
        )}

        <PaymentInformationCard
          paymentConfig={state.paymentConfig}
          orderSummary={orderSummary}
          paymentMethodOptions={paymentMethods}
        />
      </LeftColumn>

      {/* Right Column - Summary and Actions */}
      <RightColumn>
        <FinancialSummaryCard orderSummary={orderSummary} />

        {sellerRequired && (
          <Box sx={{ mt: 2, width: '100%' }}>
            <SaleSellerSelect
              locationId={state.selectedLocation?.id}
              value={state.selectedSeller}
              onChange={handleSellerChange}
              required
            />
          </Box>
        )}

        {(isEditMode ? canEditSaleDate : canModifySaleDate) && (
          <Box sx={{ mt: 2, width: '100%' }}>
            <CommercialDateModeSection
              title="Fecha de la venta"
              mode={modalState.saleDateMode}
              onModeChange={handleSaleDateModeChange}
              value={modalState.saleDate}
              onChange={setSaleDate}
              pickerLabel="Fecha y hora de la venta"
              allowKeep={isEditMode}
              currentHelperText={
                isEditMode
                  ? 'Se actualizará a la fecha y hora en que guardes los cambios.'
                  : 'Se usará la fecha y hora en que se confirme la venta.'
              }
              groupAriaLabel="Fecha comercial de la venta"
              error={saleDateModeError}
            />
          </Box>
        )}

        {isMixedSale && !isEditMode && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              backgroundColor: alpha(
                theme.palette.warning.main,
                theme.palette.mode === 'dark' ? 0.18 : 0.08,
              ),
              border: `1px solid ${alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
              borderRadius: '8px',
              padding: '12px 14px',
              mt: 2,
            })}
          >
            <InfoIcon sx={{ fontSize: 18, color: 'warning.light', mt: '2px', flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'warning.light' }}>
                Venta multi-sucursal
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Se crearán {remoteGroupCount} solicitud{remoteGroupCount === 1 ? '' : 'es'} de
                traslado automática{remoteGroupCount === 1 ? '' : 's'} para piezas de otras
                sucursales o del almacén. Las piezas locales se procesan de inmediato; las remotas
                quedan en logística hasta completar la venta.
              </Typography>
            </Box>
          </Box>
        )}

        {!canConfirmMixedSale && !isEditMode && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              backgroundColor: alpha(
                theme.palette.error.main,
                theme.palette.mode === 'dark' ? 0.18 : 0.08,
              ),
              border: `1px solid ${alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
              borderRadius: '8px',
              padding: '12px 14px',
              mt: 2,
            })}
          >
            <InfoIcon sx={{ fontSize: 18, color: 'error.light', mt: '2px', flexShrink: 0 }} />
            <Typography variant="body2" color="error.light" sx={{ lineHeight: 1.5 }}>
              No tiene permiso para confirmar ventas con piezas de otras sucursales o almacén.
            </Typography>
          </Box>
        )}

        {confirmError && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              backgroundColor: alpha(
                theme.palette.error.main,
                theme.palette.mode === 'dark' ? 0.18 : 0.08,
              ),
              border: `1px solid ${alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
              borderRadius: '8px',
              padding: '12px 14px',
              mt: 2,
            })}
          >
            <InfoIcon sx={{ fontSize: 18, color: 'error.light', mt: '2px', flexShrink: 0 }} />
            <Typography variant="body2" color="error.light" sx={{ lineHeight: 1.5 }}>
              {confirmError}
            </Typography>
          </Box>
        )}

        <ActionsBox>
          <BackButton
            fullWidth
            variant="tonal"
            onClick={previousStep}
            disabled={isProcessing}
            startIcon={<ArrowBackIcon />}
          >
            {SALES_CUSTOMER_STEP_ENABLED ? 'Regresar al Paso 3' : 'Regresar al Paso 2'}
          </BackButton>

          <ConfirmButton
            fullWidth
            variant="contained"
            onClick={openConfirmModal}
            disabled={
              isProcessing ||
              (!isEditMode && !canConfirmMixedSale) ||
              missingSeller ||
              missingSaleDate
            }
            startIcon={isProcessing ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {isProcessing ? 'Procesando...' : isEditMode ? 'Guardar cambios' : 'Confirmar Venta'}
          </ConfirmButton>
          <MissingRequirementsHint
            missingSeller={missingSeller}
            missingDate={missingSaleDate}
            sx={{ mt: -1, textAlign: 'center' }}
          />
        </ActionsBox>

        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 600, mb: 2 }}>
              {isEditMode ? 'Al guardar los cambios:' : 'Al confirmar la venta:'}
            </Typography>
            <InfoList>
              {isEditMode ? (
                <>
                  <li>Se actualizarán cliente, factura, vendedor y fecha de la venta</li>
                  {state.pricingEditable && (
                    <li>
                      Se recalculará el total y el saldo pendiente. Los pagos ya cobrados no se
                      modifican.
                    </li>
                  )}
                  <li>El inventario de piezas no se altera</li>
                </>
              ) : (
                <>
                  <li>Se registrará la venta con número único</li>
                  {isFullZeroSale && <li>No se registra pago: el total es Bs 0</li>}
                  {isMixedSale ? (
                    <>
                      <li>
                        Se crearán {remoteGroupCount} solicitud{remoteGroupCount === 1 ? '' : 'es'}{' '}
                        de traslado (SOL) hacia{' '}
                        {state.selectedLocation?.name || 'la sucursal de venta'}
                      </li>
                      <li>Las piezas de tu sucursal se actualizan de inmediato</li>
                      <li>
                        Las piezas remotas quedan en logística hasta recibir el traslado y completar
                        la venta
                      </li>
                    </>
                  ) : (
                    <li>El inventario se actualizará según las piezas entregadas</li>
                  )}
                  {!isFullZeroSale && (
                    <li>
                      {orderSummary.paymentStatus === 'complete'
                        ? isMixedSale
                          ? 'Las piezas locales pasarán a "Vendido"; las remotas al finalizar logística'
                          : 'Las piezas pasarán a estado "Vendido"'
                        : 'Se registrará el saldo pendiente'}
                    </li>
                  )}
                  <li>Podrás imprimir el comprobante</li>
                </>
              )}
            </InfoList>
          </CardContent>
        </Card>
      </RightColumn>

      <ConfirmSaleConfirmationModal
        open={showConfirmModal}
        onClose={closeConfirmModal}
        onConfirm={handleConfirmSale}
        loading={isProcessing}
        requiresZeroSaleAck={requiresZeroSaleAck}
        zeroSaleAckKind={zeroSaleAckKind}
        title={isEditMode ? '¿Guardar cambios de esta venta?' : undefined}
        message={
          isEditMode
            ? 'Se actualizarán los datos comerciales. Los pagos ya cobrados no se modifican y el inventario de piezas no se altera.'
            : undefined
        }
        confirmText={isEditMode ? 'Guardar' : undefined}
      />

      <PrintReceiptModal
        open={showPrintModal}
        onClose={handleClosePrintModal}
        orderNumber={orderNumber}
        branchReceiptNumber={branchReceiptNumber}
        orderCompletedAt={orderCompletedAt}
        selectedProducts={state.selectedProducts}
        selectedLocation={state.selectedLocation}
        orderSummary={orderSummary}
        orderCharges={state.orderCharges}
        chargeCategories={chargeCategories}
        paymentMethodOptions={paymentMethods}
        paymentConfig={state.paymentConfig}
      />
    </MainContainer>
  );
};

export default SalesStep3;
